import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("sn_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("sn_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!user) return;
    const theme = user.theme || "forest";
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("wg_theme", theme);
  }, [user]);

  const setTheme = useCallback((theme) => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("wg_theme", theme);
    setUser((u) => (u ? { ...u, theme } : u));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.needs_2fa) {
      return { needs_2fa: true, challenge_token: data.challenge_token };
    }
    localStorage.setItem("sn_token", data.token);
    setUser(data);
    // Immediately refresh from /auth/me so the user object includes fields
    // the login endpoint doesn't return (e.g. retirement_contribution).
    refresh();
    return data;
  }, [refresh]);

  const login2faVerify = useCallback(async (challenge_token, code) => {
    const { data } = await api.post("/auth/2fa/login-verify", { challenge_token, code });
    localStorage.setItem("sn_token", data.token);
    setUser(data);
    refresh();
    return data;
  }, [refresh]);

  const register = useCallback(async (email, password, name, date_of_birth, state, country, acceptTerms = false, referralCode = null) => {
    const payload = { email, password, name, accept_terms: !!acceptTerms };
    if (referralCode) payload.referral_code = referralCode;
    if (date_of_birth) payload.date_of_birth = date_of_birth;
    if (state) payload.state = state;
    if (country) payload.country = country;
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("sn_token", data.token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("sn_token");
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, login2faVerify, register, logout, refresh, setTheme }),
    [user, loading, login, login2faVerify, register, logout, refresh, setTheme],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
